import { setCompany } from "../../redux/features/manager/managerSlice";
import { fetchCurrentPlan } from "../../redux/features/subscription/subscriptionSlice";
import { fetchUser, switchApp } from "../../redux/features/user/userSlice";
import { getProjectByAppId } from "../../projectConfig/projects";
import { getProjectSwitchUrl } from "../environment";
import { isLocalhost } from "../appIdStorage";
import {
  WORKSPACE_SWITCH_FALLBACK_PROFILE_PATH,
  resolveWorkspaceSwitchDestination,
} from "./routeSafetyMap";

function buildCrossDomainNavigationTarget(navigation, appId) {
  const destination = resolveWorkspaceSwitchDestination({ navigation });
  if (isLocalhost()) {
    return destination;
  }

  const project = getProjectByAppId(appId);
  const baseUrl = getProjectSwitchUrl(appId) ?? project?.href ?? "";
  const normalizedBase = String(baseUrl).replace(/\/$/, "");
  const normalizedPath = destination.startsWith("/") ? destination : `/${destination}`;

  return `${normalizedBase}${normalizedPath}`;
}

function syncSelectedCompanyStorage(companyId) {
  if (!Number.isFinite(companyId) || companyId <= 0) {
    return;
  }
  localStorage.setItem("selectedCompany", JSON.stringify(companyId));
}

async function switchCompanyContext(dispatch, companyId) {
  if (!Number.isFinite(companyId) || companyId <= 0) {
    return { ok: false, error: "Invalid company_id" };
  }

  try {
    await dispatch(setCompany({ company_id: companyId })).unwrap();
  } catch (error) {
    return {
      ok: false,
      error: error?.message || "Failed to switch company",
    };
  }

  try {
    await dispatch(fetchUser()).unwrap();
  } catch (error) {
    return {
      ok: false,
      error: error?.message || "Failed to refresh user after company switch",
    };
  }

  // Session company is now the new one; refresh plan so dashboard/billing
  // do not keep showing the previous company's dates.
  await dispatch(fetchCurrentPlan());

  syncSelectedCompanyStorage(companyId);

  return { ok: true, error: null };
}

async function switchAppContext(dispatch, appId) {
  if (!Number.isFinite(appId) || appId <= 0) {
    return { ok: true, error: null };
  }

  try {
    await dispatch(switchApp(appId)).unwrap();
  } catch (error) {
    return {
      ok: false,
      error: error?.message || "Failed to switch app",
    };
  }

  localStorage.setItem("app_id", String(appId));
  window.dispatchEvent(new Event("appIdChanged"));

  return { ok: true, error: null };
}

export async function switchWorkspaceContext(
  dispatch,
  options = {},
  helpers = {},
) {
  const {
    company_id: companyId,
    app_id: appId,
    navigation = null,
    preserveCurrentRoute = false,
  } = options;

  const getCurrentPathname =
    typeof helpers.getCurrentPathname === "function"
      ? helpers.getCurrentPathname
      : () => "";
  const getCurrentSearch =
    typeof helpers.getCurrentSearch === "function" ? helpers.getCurrentSearch : () => "";

  if (typeof helpers.hasDirtyForm === "function" && helpers.hasDirtyForm()) {
    return {
      status: "cancelled",
      destination: null,
      error: "Dirty form guard (Phase 5 — not implemented)",
    };
  }

  if (companyId != null) {
    const companyResult = await switchCompanyContext(dispatch, Number(companyId));
    if (!companyResult.ok) {
      return {
        status: "error",
        destination: null,
        error: companyResult.error,
      };
    }
  }

  if (appId != null) {
    const appResult = await switchAppContext(dispatch, Number(appId));
    if (!appResult.ok) {
      return {
        status: "error",
        destination: null,
        error: appResult.error,
      };
    }
  }

  const destination = navigation?.path
    ? buildCrossDomainNavigationTarget(navigation, appId)
    : resolveWorkspaceSwitchDestination({
        pathname: getCurrentPathname(),
        search: getCurrentSearch(),
        preserveCurrentRoute,
        navigation,
      });

  const safeDestination = destination || WORKSPACE_SWITCH_FALLBACK_PROFILE_PATH;

  if (typeof helpers.navigate === "function" && isLocalhost()) {
    helpers.navigate(safeDestination);
  } else if (!isLocalhost() && (appId != null || navigation?.path)) {
    window.location.href = safeDestination;
  } else if (typeof helpers.navigate === "function") {
    helpers.navigate(safeDestination);
  }

  return {
    status: "success",
    destination: safeDestination,
    error: null,
  };
}
